import React, { useEffect, useState } from "react";
import { Phone, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { ringcentralStatus, ringOut, importCallRecording } from "../../lib/api";

// Click-to-call via RingCentral, then pull the recording and transcribe it.
// If onTranscript is provided, the transcript is handed back for editing (not auto-saved).
// Otherwise the backend auto-saves it as a call note for the given client.
export const ClickToCall = ({ token, toNumber, clientId, clientName, onTranscript }) => {
  const [configured, setConfigured] = useState(null);
  const [calling, setCalling] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [called, setCalled] = useState(false);

  useEffect(() => {
    if (token) ringcentralStatus(token).then((r) => setConfigured(r.configured)).catch(() => setConfigured(false));
  }, [token]);

  const call = async () => {
    if (!toNumber) { toast.error("This client has no phone number on file."); return; }
    if (!configured) { toast.info("RingCentral isn't connected yet. Add your credentials to enable calling."); return; }
    setCalling(true);
    try {
      await ringOut(token, { to_number: toNumber, client_id: clientId, client_name: clientName });
      setCalled(true);
      toast.success("Calling… your phone will ring first, then connect to the client.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the call.");
    } finally {
      setCalling(false);
    }
  };

  const pull = async () => {
    setPulling(true);
    try {
      const res = await importCallRecording(token, {
        to_number: toNumber,
        client_id: onTranscript ? null : clientId,
        client_name: clientName,
      });
      if (onTranscript) {
        onTranscript(res.transcript);
        toast.success("Transcript ready — review and save it.");
      } else {
        toast.success("Call transcribed and saved as a note.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import the recording.");
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2" data-testid="click-to-call">
      <button
        type="button"
        onClick={call}
        disabled={calling}
        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
        title={configured === false ? "RingCentral not connected yet" : "Call this client"}
        data-testid="click-to-call-button"
      >
        {calling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />} Click to Call
      </button>
      {called && (
        <button
          type="button"
          onClick={pull}
          disabled={pulling}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-navy/5 text-navy hover:bg-navy/10 disabled:opacity-50 transition-colors"
          title="Pull the call recording and transcribe it"
          data-testid="pull-recording-button"
        >
          {pulling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Get transcript
        </button>
      )}
    </div>
  );
};
